import './App.scss';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import React, { useState, useEffect, Suspense, lazy } from 'react';
import RequireAuth from './components/RequireAuth';
import RequireCompanyFeature from './components/RequireCompanyFeature';
import { CompanyFeature } from './config/companyFeatures';
import RedirectIfAuthenticated from './components/RedirectIfAuthenticated';
import useRefreshToken from './hooks/useRefreshToken';
import useFeatureFlags from './hooks/useFeatureFlags';
import useAxiosPrivate from './hooks/useAxiosPrivate';

import { useSelector } from 'react-redux';
import { ROLES } from './state/auth/types';
import { PORTAL_ROLES, isViewer, selectAuth } from './state/auth';
const { ARTYC_ADMIN, ARTYC_EMPLOYEE, COMPANY_ADMIN, ADMIN, VIEWER } = ROLES;

// Viewers are read-only and only get the Journeys pages; every other portal
// page is for the remaining roles.
const NON_VIEWER_PORTAL_ROLES = PORTAL_ROLES.filter(
  (role) => role.pid !== VIEWER.pid
);

import { ToastContainer } from './aurora/components/Toast';
import { Text } from './aurora/typography/Text/Text';
import FeatureFlagsApi from './api/featureFlagsApi';
import PostHogIdentifier from './analytics/PostHogIdentifier';
import usePageviews from './analytics/usePageviews';

// Route components are code-split so the initial bundle doesn't have to
// ship mapbox-gl, chart.js, react-to-pdf, @turf/turf, etc. for visitors
// who land on /login. Each lazy() call creates a separate webpack chunk
// that's fetched only when the route is matched. See SOF-1995.
const Portal = lazy(() => import('./components/layout/Portal'));
const Unauthorized = lazy(() => import('./pages/Unauthorized'));
const Missing = lazy(() => import('./pages/Missing'));
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('./pages/auth/RegisterPage'));
const ForgotPasswordPage = lazy(
  () => import('./pages/auth/ForgotPasswordPage')
);
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage'));
const RegisterConfirmationPage = lazy(
  () => import('./pages/auth/RegisterConfirmationPage')
);
const PasswordResetConfirmationPage = lazy(
  () => import('./pages/auth/PasswordResetConfirmationPage')
);
const MfaPage = lazy(() => import('./pages/auth/MfaPage'));
const SessionLockedPage = lazy(() => import('./pages/auth/SessionLockedPage'));
const SupportFormPage = lazy(() => import('./pages/support/SupportFormPage'));
const DevicesPage = lazy(() => import('./pages/devices/DevicesPage'));
const UsersPage = lazy(() => import('./pages/users/UsersPage'));
const FieldPersonnelPage = lazy(
  () => import('./pages/fieldPersonnel/FieldPersonnelPage')
);
const GroupsPage = lazy(() => import('./pages/groups/GroupsPage'));
const HomePage = lazy(() => import('./pages/home/HomePage'));
const JourneysPage = lazy(() => import('./pages/journeys/JourneysPage'));
const JourneyPage = lazy(() => import('./pages/journeys/JourneyPage'));
const CompaniesPage = lazy(() => import('./pages/companies/CompaniesPage'));
const EventsPage = lazy(() => import('./pages/events/EventsPage'));
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage'));
const FleetViewPage = lazy(() => import('./pages/fleetView/FleetViewPage'));
const CompanyProfilePage = lazy(
  () => import('./pages/companyProfile/CompanyProfilePage')
);
const CustomerCostReportsPage = lazy(
  () => import('./pages/customerCostReports/CustomerCostReportsPage')
);
const CostCalculatorPage = lazy(
  () => import('./pages/customerCostReports/CostCalculatorPage')
);
const CostReportPage = lazy(
  () => import('./pages/customerCostReports/CostReportPage')
);
const PcbTestsPage = lazy(() => import('./pages/pcbTests/PcbTestsPage'));
const ArtycAIPage = lazy(() => import('./pages/artycAI/ArtycAIPage'));
const OrdersPage = lazy(() => import('./pages/orders/OrdersPage'));
const AuditTrailPage = lazy(() => import('./pages/auditTrail/AuditTrailPage'));
const LaneAtlasPage = lazy(() => import('./pages/laneAtlas/LaneAtlasPage'));

const App = () => {
  const [isAuthChecked, setIsAuthChecked] = useState(false);
  const { updateFeatureFlags, isLoading } = useFeatureFlags();
  const axiosPrivate = useAxiosPrivate();
  const auth = useSelector(selectAuth);
  const viewer = isViewer(auth);

  const refresh = useRefreshToken();
  const location = useLocation();

  // Capture a sanitized $pageview on every route change.
  usePageviews();

  const isOpenRoutePath = (path: string): boolean =>
    path === '/login' ||
    path === '/auth/mfa' ||
    path === '/forgot-password' ||
    /^\/register\/[^/]+$/.test(path) ||
    /^\/reset-password\/[^/]+$/.test(path) ||
    /^\/cost-report\/[^/]+$/.test(path);

  // Keep per-user feature flags fresh when the tab regains focus, so a grant or
  // revoke made elsewhere reflects without a full reload.
  useEffect(() => {
    const refetchFlags = () => {
      if (isOpenRoutePath(window.location.pathname)) return;
      updateFeatureFlags(() => FeatureFlagsApi.getFeatureFlags(axiosPrivate), {
        silent: true,
      }).catch(() => undefined);
    };
    window.addEventListener('focus', refetchFlags);
    return () => window.removeEventListener('focus', refetchFlags);
  }, []);

  useEffect(() => {
    const initialize = async () => {
      // Always check authentication regardless of route
      try {
        const path = location.pathname;
        const isOpenRoute = isOpenRoutePath(path);

        if (isOpenRoute) {
          await refresh({ redirectOnFail: false });
        } else {
          await refresh(); // default redirects to login on fail
        }

        // Fetch feature flags on any authenticated route so per-user flags
        // (e.g. OTA) are available wherever they gate UI, including deep links.
        if (!isOpenRoute) {
          try {
            await updateFeatureFlags(() =>
              FeatureFlagsApi.getFeatureFlags(axiosPrivate)
            );
          } catch (error) {
            console.error('Error fetching feature flags:', error);
          }
        }
      } catch (error) {
        // Token refresh failed - user is not authenticated
        console.error('Error refreshing token:', error);
      }
      setIsAuthChecked(true);
    };

    initialize();
  }, []);

  if (!isAuthChecked || isLoading) {
    return (
      <Text size="md" sx={{ margin: '12px' }}>
        Loading...
      </Text>
    );
  }

  return (
    <div className="app">
      <PostHogIdentifier />
      <div className="appContainer" id="app">
        <Suspense
          fallback={
            <Text size="md" sx={{ margin: '12px' }}>
              Loading...
            </Text>
          }
        >
          <Routes>
            {/* open routes */}
            <Route path="cost-report/:id" element={<CostReportPage />} />
            <Route
              path="/"
              element={<Navigate to={'/portal/home'} replace />}
            />

            {/* open routes - redirect if authenticated */}
            <Route element={<RedirectIfAuthenticated />}>
              <Route path="login" element={<LoginPage />} />
              <Route path="auth/mfa" element={<MfaPage />} />
              <Route
                path="auth/session-locked"
                element={<SessionLockedPage />}
              />
              <Route path="register/:token" element={<RegisterPage />} />
              <Route path="forgot-password" element={<ForgotPasswordPage />} />
              <Route
                path="reset-password/:token"
                element={<ResetPasswordPage />}
              />
            </Route>

            {/* protected routes */}

            <Route element={<RequireAuth allowedRoles={PORTAL_ROLES} />}>
              <Route
                path="registration-confirmation"
                element={<RegisterConfirmationPage />}
              />
              <Route
                path="password-reset-success"
                element={<PasswordResetConfirmationPage />}
              />
            </Route>

            {/* portal routes */}
            <Route path="/portal/" element={<Portal />}>
              <Route element={<RequireAuth allowedRoles={PORTAL_ROLES} />}>
                <Route
                  path=""
                  element={<Navigate to={'/portal/home'} replace />}
                />
                {/* Viewers land on Journeys — their only page. */}
                <Route
                  path="home"
                  element={
                    viewer ? (
                      <Navigate to={'/portal/journeys'} replace />
                    ) : (
                      <HomePage />
                    )
                  }
                  index
                />
                <Route path="shipments" element={<JourneysPage />} />
                <Route path="journeys" element={<JourneysPage />} />
                <Route path="journey/:journey" element={<JourneyPage />} />
                <Route path="support" element={<SupportFormPage />} />
              </Route>

              <Route
                element={<RequireAuth allowedRoles={NON_VIEWER_PORTAL_ROLES} />}
              >
                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="fleet-view" element={<FleetViewPage />} />
                <Route
                  element={
                    <RequireCompanyFeature feature={CompanyFeature.ARTYC_AI} />
                  }
                >
                  <Route path="artyc-ai" element={<ArtycAIPage />} />
                </Route>
                <Route path="shippers" element={<DevicesPage />} />
                <Route path="events" element={<EventsPage />} />
                <Route path="orders" element={<OrdersPage />} />
              </Route>

              <Route
                element={
                  <RequireAuth
                    allowedRoles={[ARTYC_ADMIN, ARTYC_EMPLOYEE, COMPANY_ADMIN]}
                  />
                }
              >
                <Route path="groups" element={<GroupsPage />} />
              </Route>

              <Route
                element={
                  <RequireAuth
                    allowedRoles={[
                      ARTYC_ADMIN,
                      ARTYC_EMPLOYEE,
                      COMPANY_ADMIN,
                      ADMIN,
                    ]}
                  />
                }
              >
                <Route path="users" element={<UsersPage />} />
                <Route
                  element={
                    <RequireCompanyFeature
                      feature={CompanyFeature.AUDIT_TRAIL}
                    />
                  }
                >
                  <Route path="audit-trail" element={<AuditTrailPage />} />
                </Route>
              </Route>

              <Route
                element={
                  <RequireAuth
                    allowedRoles={[ARTYC_ADMIN, ARTYC_EMPLOYEE, COMPANY_ADMIN]}
                  />
                }
              >
                <Route
                  path="company-profile"
                  element={<CompanyProfilePage />}
                />
                <Route
                  element={
                    <RequireCompanyFeature
                      feature={CompanyFeature.FIELD_PERSONNEL}
                    />
                  }
                >
                  <Route
                    path="field-personnel"
                    element={<FieldPersonnelPage />}
                  />
                </Route>
              </Route>

              <Route
                element={
                  <RequireAuth allowedRoles={[ARTYC_ADMIN, ARTYC_EMPLOYEE]} />
                }
              >
                <Route path="pcb-tests" element={<PcbTestsPage />} />
                <Route path="lane-atlas" element={<LaneAtlasPage />} />
                <Route path="companies" element={<CompaniesPage />} />
                <Route
                  path="customer-cost-reports"
                  element={<CustomerCostReportsPage />}
                />
                <Route
                  path="cost-calculator"
                  element={<CostCalculatorPage />}
                />
                <Route
                  path="cost-calculator/:id"
                  element={<CostCalculatorPage />}
                />
              </Route>
            </Route>

            <Route path="unauthorized" element={<Unauthorized />} />
            <Route path="*" element={<Missing />} />
          </Routes>
        </Suspense>
      </div>
      <div className="toastContainer">
        <ToastContainer />
      </div>
      <div id="modalContainer" />
    </div>
  );
};

export default App;
